/**
 * wagmiConfig.js — Configuration Wagmi v2 pour EarniWalletModal.
 *
 * 4 connecteurs supportés (Phase 1 §8.6) :
 *   - MetaMask       : injected, discriminé par window.ethereum.isMetaMask
 *   - Coinbase Wallet: connecteur officiel, supporte extension + QR fallback
 *   - TrustWallet    : injected, cible window.trustwallet puis window.ethereum.isTrust
 *   - Binance Web3   : injected, cible window.BinanceChain (≠ window.ethereum)
 *
 * Chain : Avalanche Mainnet (prod) ou Avalanche Fuji (staging si REACT_APP_PROD_ENV=staging).
 * ChainId lu depuis REACT_APP_AVAX_CHAIN_ID — accepte hex (0xa869/0xa86a) ET décimal.
 *
 * §8.7 — Pas de persistance de session wagmi (auth state géré par Redux + localStorage).
 */
import { createConfig, http }        from 'wagmi';
import { avalanche, avalancheFuji }  from 'wagmi/chains';
import { injected, coinbaseWallet }  from 'wagmi/connectors';

/* ─── Détection de l'environnement ─────────────────────────────────────────── */
const isStaging = process.env.REACT_APP_PROD_ENV === 'staging';
/* La chaîne active EST la source unique de vérité (staging = Avalanche Fuji testnet,
 * prod = Avalanche mainnet). Exportée pour que header/switchNetwork/messages s'y
 * réfèrent au lieu de relire REACT_APP_AVAX_CHAIN_ID, qui pouvait diverger de la
 * chaîne réellement connectée par wagmi (ex : env=43114 mainnet alors que staging
 * force Fuji 43113) → faux « mauvais réseau ». */
export const activeChain = isStaging ? avalancheFuji : avalanche;
const rpcUrl = isStaging
  ? (process.env.REACT_APP_FUJI_RPC_URL      || 'https://api.avax-test.network/ext/bc/C/rpc')
  : (process.env.REACT_APP_AVALANCHE_RPC_URL || 'https://api.avax.network/ext/bc/C/rpc');

/* ─── ChainId attendu — dérivé de la chaîne active (plus de lecture d'env divergente) ── */
export const resolvedChainId = activeChain.id;                          // ex: 43113 (Fuji) / 43114 (mainnet)
export const EXPECTED_CHAIN_ID_HEX = `0x${activeChain.id.toString(16)}`; // ex: '0xa869' / '0xa86a'
/* Libellé lisible pour les messages "mauvais réseau" — précise « Testnet » sur Fuji
 * (sinon l'utilisateur croit qu'« Avalanche » = mainnet et se trompe de réseau). */
export const NETWORK_LABEL = `${activeChain.name}${activeChain.testnet ? ' (Testnet)' : ''}`;
/* Paramètres wallet_addEthereumChain (fallback si la chaîne n'est pas dans le wallet). */
export const ADD_CHAIN_PARAMS = {
  chainId: EXPECTED_CHAIN_ID_HEX,
  chainName: activeChain.name,
  nativeCurrency: activeChain.nativeCurrency,
  rpcUrls: activeChain.rpcUrls?.default?.http?.length ? [...activeChain.rpcUrls.default.http] : [rpcUrl],
  blockExplorerUrls: activeChain.blockExplorers?.default?.url ? [activeChain.blockExplorers.default.url] : [],
};

/* ─── MetaMask — discriminé par isMetaMask pour éviter la collision
 *    quand plusieurs wallets injectent dans window.ethereum ─────────────────── */
const metaMaskConnector = injected({
  target: {
    id: 'metaMask',
    name: 'MetaMask',
    provider: () =>
      typeof window !== 'undefined' && window.ethereum?.isMetaMask
        ? window.ethereum
        : undefined,
  },
});

/* ─── Coinbase Wallet — supporte extension + QR code fallback ──────────────── */
const coinbaseConnector = coinbaseWallet({
  appName: 'Earniverse Marketplace',
  appLogoUrl: typeof window !== 'undefined'
    ? `${window.location.origin}/logo192.png`
    : '',
  preference: 'all',
});

/* ─── TrustWallet — cible window.trustwallet en priorité,
 *    fallback window.ethereum.isTrust ─────────────────────────────────────── */
const trustWalletConnector = injected({
  target: {
    id: 'trustWallet',
    name: 'Trust Wallet',
    provider: () => {
      if (typeof window === 'undefined') return undefined;
      return window.trustwallet
        ?? (window.ethereum?.isTrust ? window.ethereum : undefined);
    },
  },
});

/* ─── Binance Web3 Wallet — injecte dans window.BinanceChain (≠ window.ethereum) */
const binanceConnector = injected({
  target: {
    id: 'binanceWallet',
    name: 'Binance Web3 Wallet',
    provider: () =>
      typeof window !== 'undefined' ? window.BinanceChain : undefined,
  },
});

/* ─── Configuration Wagmi principale ────────────────────────────────────────── */
export const wagmiConfig = createConfig({
  chains:           [activeChain],
  connectors:       [
    metaMaskConnector,
    coinbaseConnector,
    trustWalletConnector,
    binanceConnector,
  ],
  transports:       { [activeChain.id]: http(rpcUrl) },
  storage:          null,          // auth state géré par Redux + localStorage (§8.7)
  reconnectOnMount: false,         // le flow SIWE pilote l'auth ; pas de session wagmi persistée
});

/**
 * Liste ordonnée des connecteurs avec métadonnées.
 * Utilisée par WalletPickerPanel pour afficher les 4 boutons.
 *
 * @type {Array<{id: string, name: string, icon: string}>}
 */
export const WALLET_CONNECTORS_META = [
  { id: 'metaMask',      name: 'MetaMask',            icon: '/assets/wallets/metamask.png'  },
  { id: 'coinbaseWallet',name: 'Coinbase Wallet',      icon: '/assets/wallets/coinbase.png'  },
  { id: 'trustWallet',   name: 'Trust Wallet',         icon: '/assets/wallets/trustwallet.png' },
  { id: 'binanceWallet', name: 'Binance Web3 Wallet',  icon: '/assets/wallets/binance.png'   },
];
